import { BaseRepository, QueryOptions, RepositoryError } from './BaseRepository';

/**
 * Customer entity interface
 * Represents a unified customer across all systems
 */
export interface Customer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  company_name: string | null;
  created_by_company_id: string | null;
  email: string | null;
  phone_number: string | null;
  commercial: boolean;
  address_1: string | null;
  address_2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  full_address: string | null;
  balance_due: number | null;
  active_agreements_count: number;
  open_jobs_count: number;
  total_jobs_count: number;
  last_job_date: string | null;
  sw_customer_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Filter options for customer queries
 */
export interface CustomerFilters {
  search?: string;
  commercial?: boolean;
  hasBalance?: boolean;
  hasActiveAgreements?: boolean;
  hasOpenJobs?: boolean;
  city?: string;
  state?: string;
  companyId?: string;
}

/**
 * Customer statistics
 */
export interface CustomerStatistics {
  total: number;
  commercial: number;
  residential: number;
  totalBalance: number;
  totalAgreements: number;
  totalOpenJobs: number;
}

/**
 * Customer Repository
 * 
 * Manages all customer data access operations.
 * During database consolidation, only the table name needs to change here.
 * 
 * @example
 * ```typescript
 * const customerRepo = new CustomerRepository();
 * 
 * // Find all customers
 * const customers = await customerRepo.findAll();
 * 
 * // Search with filters
 * const results = await customerRepo.findByFilters({
 *   search: 'john',
 *   commercial: false,
 *   hasBalance: true
 * });
 * 
 * // Get statistics
 * const stats = await customerRepo.getStatistics();
 * ```
 */
export class CustomerRepository extends BaseRepository<Customer> {
  constructor() {
    super({
      tableName: 'customers', // Single source of truth per database consolidation
      primaryKey: 'id'
    });
  }

  /**
   * Find customers by various filters with advanced search capabilities
   * 
   * @param filters Customer-specific filter options
   * @param options Query options (pagination, ordering)
   * @returns Promise resolving to array of matching customers
   */
  async findByFilters(
    filters: CustomerFilters, 
    options?: QueryOptions
  ): Promise<Customer[]> {
    let query = this.client.from(this.config.tableName).select('*');

    // Advanced search across multiple fields
    if (filters.search) {
      query = query.or(`
        full_name.ilike.%${filters.search}%,
        company_name.ilike.%${filters.search}%,
        email.ilike.%${filters.search}%,
        phone_number.ilike.%${filters.search}%,
        city.ilike.%${filters.search}%,
        zip_code.ilike.%${filters.search}%
      `);
    }

    // Commercial filter
    if (filters.commercial !== undefined) {
      query = query.eq('commercial', filters.commercial);
    }

    // Balance filter
    if (filters.hasBalance) {
      query = query.gt('balance_due', 0);
    }

    // Active agreements filter
    if (filters.hasActiveAgreements) {
      query = query.gt('active_agreements_count', 0);
    }

    // Open jobs filter
    if (filters.hasOpenJobs) {
      query = query.gt('open_jobs_count', 0);
    }

    // City filter
    if (filters.city) {
      query = query.ilike('city', `%${filters.city}%`);
    }

    // State filter
    if (filters.state) {
      query = query.eq('state', filters.state);
    }

    // Company ID filter
    if (filters.companyId) {
      if (filters.companyId === 'none') {
        query = query.is('created_by_company_id', null);
      } else {
        query = query.eq('created_by_company_id', filters.companyId);
      }
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      // Default ordering
      query = query.order('updated_at', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to fetch customers with filters`, 
        error
      );
    }

    return (data || []) as Customer[];
  }

  /**
   * Get customer statistics
   * 
   * @returns Promise resolving to customer statistics
   */
  async getStatistics(): Promise<CustomerStatistics> {
    // Use count() to get actual total without fetching all rows
    const total = await this.count();
    const commercial = await this.count({ commercial: true });
    const residential = await this.count({ commercial: false });

    // For aggregations (balance, agreements, jobs), we need to fetch data
    // But only fetch the specific fields we need for aggregation
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('balance_due, active_agreements_count, open_jobs_count');

    if (error) {
      throw new RepositoryError(
        `Failed to fetch customer statistics`, 
        error
      );
    }

    const customers = data || [];
    
    return {
      total,
      commercial,
      residential,
      totalBalance: customers.reduce((sum, c) => sum + (c.balance_due || 0), 0),
      totalAgreements: customers.reduce((sum, c) => sum + (c.active_agreements_count || 0), 0),
      totalOpenJobs: customers.reduce((sum, c) => sum + (c.open_jobs_count || 0), 0)
    };
  }

  /**
   * Get customer breakdown by company
   * 
   * @returns Promise resolving to company breakdown with company names as keys
   */
  async getCompanyBreakdown(): Promise<Record<string, number>> {
    // Query actual sw_companies to get real company IDs and names
    const { data: companies, error: companiesError } = await this.client
      .from('sw_companies')
      .select('id, name')
      .eq('is_active', true);

    if (companiesError) {
      throw new RepositoryError(
        `Failed to fetch companies for breakdown`, 
        companiesError
      );
    }

    const breakdown: Record<string, number> = {};

    // Count for each active company using actual UUIDs
    for (const company of (companies || [])) {
      const count = await this.count({ created_by_company_id: company.id });
      // Use company name as key (e.g., "Hales AC", "Climate Control Services")
      breakdown[company.name] = count;
    }

    // Count customers with no company (Right Away Home Warranty)
    const nullCount = await this.countByNullCompanyId();
    breakdown['Right Away Home Warranty'] = nullCount;

    return breakdown;
  }

  /**
   * Count customers with null created_by_company_id
   * 
   * @returns Promise resolving to count
   */
  private async countByNullCompanyId(): Promise<number> {
    const { count, error } = await this.client
      .from(this.config.tableName)
      .select('*', { count: 'exact', head: true })
      .is('created_by_company_id', null);

    if (error) {
      throw new RepositoryError(
        `Failed to count customers with null created_by_company_id`, 
        error
      );
    }

    return count || 0;
  }

  /**
   * Find customer by email address
   * 
   * @param email Email address to search for
   * @returns Promise resolving to customer or null
   */
  async findByEmail(email: string): Promise<Customer | null> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new RepositoryError(
        `Failed to find customer by email`, 
        error
      );
    }

    return data as Customer;
  }

  /**
   * Find customers by phone number
   * Note: Returns array since multiple customers may share a phone
   * 
   * @param phone Phone number to search for
   * @returns Promise resolving to array of customers
   */
  async findByPhone(phone: string): Promise<Customer[]> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('phone_number', phone);

    if (error) {
      throw new RepositoryError(
        `Failed to find customers by phone`, 
        error
      );
    }

    return (data || []) as Customer[];
  }

  /**
   * Find customers by company ID
   * 
   * @param companyId Company ID to filter by
   * @param options Query options
   * @returns Promise resolving to array of customers
   */
  async findByCompany(
    companyId: string | null, 
    options?: QueryOptions
  ): Promise<Customer[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*');

    if (companyId === null) {
      query = query.is('created_by_company_id', null);
    } else {
      query = query.eq('created_by_company_id', companyId);
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find customers by company`, 
        error
      );
    }

    return (data || []) as Customer[];
  }

  /**
   * Find customers with outstanding balance
   * 
   * @param minBalance Minimum balance threshold (default: 0)
   * @param options Query options
   * @returns Promise resolving to array of customers
   */
  async findWithBalance(
    minBalance: number = 0,
    options?: QueryOptions
  ): Promise<Customer[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .gt('balance_due', minBalance);

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      // Default: order by balance descending
      query = query.order('balance_due', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find customers with balance`, 
        error
      );
    }

    return (data || []) as Customer[];
  }

  /**
   * Search customers by name (fuzzy search)
   * 
   * @param nameQuery Search query
   * @param options Query options
   * @returns Promise resolving to array of customers
   */
  async searchByName(
    nameQuery: string,
    options?: QueryOptions
  ): Promise<Customer[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .or(`full_name.ilike.%${nameQuery}%,company_name.ilike.%${nameQuery}%`);

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to search customers by name`, 
        error
      );
    }

    return (data || []) as Customer[];
  }
}
